/**
 * useProviderAccounts — maps `provider_integration_id` → { provider, email } for
 * rendering contact provenance icons/badges (§3.5). Shares the integrations-list
 * query cache (same key as ContactsAccountsModal / useContactScope), so it's warm.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '@/features/auth/hooks/useSession';
import { queryKeys } from '@/lib/query/keys';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';
import { listIntegrations } from '@/lib/api/endpoints/integrations';

export interface ProviderAccount {
  provider: string;
  /** Account display name (the connected account's email). */
  email: string;
}

export function useProviderAccounts(): Map<string, ProviderAccount> {
  const { token } = useSession();
  const workspaceId = getActiveWorkspaceId();
  const { data } = useQuery({
    queryKey: queryKeys.integrations.list(workspaceId),
    queryFn: () => listIntegrations(token),
    enabled: Boolean(token),
    staleTime: 30_000,
  });
  return useMemo(() => {
    const map = new Map<string, ProviderAccount>();
    for (const i of data ?? []) map.set(i.id, { provider: i.provider, email: i.name || i.id });
    return map;
  }, [data]);
}
